import type { CompiledFrameworkStructure } from "@repo/shared";
import { parseProjectPath } from "@repo/shared";
import { Clock3, FolderTree, GitBranch, Link2 } from "lucide-react";
import type { ElementType } from "react";
import type {
  ReferencePresentationInput,
  ReferencePresentationResolver,
} from "@/features/rich-text";
import {
  defaultReferencePresentationResolver,
  normalizeReferenceTargetKind,
  resolveWellKnownMentionIcon,
} from "@/features/rich-text";
import { toInternalAppPath } from "@/lib/internal-link-label";
import { resolveEntityDisplayIcon } from "./entity-icon-utils";
import { buildEntityDisplayMap, buildFrameworkEntityAliases } from "./framework-registry";
import { resolveFrameworkIcon } from "./icon-registry";

type ProjectReferenceStructure = Pick<
  CompiledFrameworkStructure,
  "rootFolders" | "folderNodes" | "entityDisplays"
>;

export function createProjectReferencePresentationResolver(args: {
  frameworkStructure: ProjectReferenceStructure | null | undefined;
  projectResourceId: string;
}): ReferencePresentationResolver {
  const structure = args.frameworkStructure ?? null;
  const entityDisplays = buildEntityDisplayMap(structure);
  const aliases = buildFrameworkEntityAliases(structure);

  const resolveEntityIcon = (entityTypeKey: string, iconKey?: string | null): ElementType => {
    const display = entityDisplays.get(entityTypeKey);
    return resolveEntityDisplayIcon({
      entityTypeKey,
      iconKey: iconKey ?? display?.iconKey,
    });
  };

  const resolveProjectHrefIcon = (href: string | undefined): ElementType | null => {
    if (!href) return null;
    const projectPath = getProjectRelativePath({
      href,
      projectResourceId: args.projectResourceId,
    });
    if (!projectPath || !aliases) return null;

    const parsedPath = parseProjectPath(projectPath, { aliases, structure });
    if (parsedPath.versionIntent?.kind === "preview") {
      return Clock3;
    }
    if (parsedPath.versionIntent?.kind === "diff") {
      return GitBranch;
    }
    if (parsedPath.routeKey === "entity") {
      const entityTypeKey = parsedPath.params.entityTypeKey;
      return entityTypeKey ? resolveEntityIcon(entityTypeKey) : null;
    }
    // `welcome` is the project root path `/`, which is a *place* in the project however it is
    // rendered — it borrowed the Documents screen between LAT-870 and LAT-1004 and now has the
    // Project Home screen of its own, and neither changed what a link to it references, so the
    // location icon is still the right answer. `/documents` is deliberately not folded in here:
    // it is a screen, not a location in the tree.
    if (parsedPath.routeKey === "folder" || parsedPath.routeKey === "welcome") {
      return FolderTree;
    }
    return null;
  };

  return (input: ReferencePresentationInput) => {
    const targetKind = normalizeReferenceTargetKind(input.targetKind);
    const entityTypeKey = input.entityTypeKey ?? input.mentionType;
    const iconKey = input.iconKey ?? null;

    // A faceted mention wins over the entity's own icon: a chip that points at version 3, or
    // at the change between 2 and 3, reads as a version reference first and as a dataset
    // second — the same way a `/ver/3` link already does. Plain mentions are unaffected.
    if (input.kind === "mention" && (targetKind === "version" || targetKind === "diff")) {
      return { Icon: targetKind === "version" ? Clock3 : GitBranch, iconKey };
    }

    if (input.kind === "mention") {
      const wellKnownIcon = resolveWellKnownMentionIcon(input.mentionType);
      if (wellKnownIcon) return { Icon: wellKnownIcon };
      if (entityTypeKey) {
        return { Icon: resolveEntityIcon(entityTypeKey, iconKey), iconKey };
      }
    }

    if (input.kind === "link") {
      if (input.entityTypeKey) {
        return { Icon: resolveEntityIcon(input.entityTypeKey, iconKey), iconKey };
      }
      const hrefIcon = resolveProjectHrefIcon(input.href);
      if (hrefIcon) {
        return { Icon: hrefIcon, iconKey };
      }
      if (targetKind === "entity") return { Icon: resolveFrameworkIcon(iconKey, Link2), iconKey };
      if (targetKind === "location") return { Icon: FolderTree, iconKey };
      if (targetKind === "version") return { Icon: Clock3, iconKey };
      if (targetKind === "diff") return { Icon: GitBranch, iconKey };
      return { Icon: Link2, iconKey };
    }

    if (input.mentionType && entityDisplays.has(input.mentionType)) {
      return { Icon: resolveEntityIcon(input.mentionType, iconKey), iconKey };
    }
    if (input.mentionType) {
      return {
        Icon: resolveEntityDisplayIcon({ entityTypeKey: input.mentionType, iconKey }),
        iconKey,
      };
    }

    return defaultReferencePresentationResolver(input);
  };
}

function getProjectRelativePath(args: { href: string; projectResourceId: string }): string | null {
  const internalPath = toInternalAppPath(args.href);
  if (!internalPath) return null;

  const path = internalPath.split("?")[0]?.split("#")[0] ?? internalPath;
  const segments = path
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
  if (segments[0] !== "project" || segments[1] !== args.projectResourceId) {
    return null;
  }

  const relativeSegments = segments.slice(2);
  return relativeSegments.length > 0 ? `/${relativeSegments.join("/")}` : "/";
}
